import { auth } from '../lib/firebase';
import { normalizeBackendUrl } from './backendUrl';
import type { UsdaPortionCandidate } from '../types/unitConversion';

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL);

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Vous devez être connecté en admin.');
  return user.getIdToken();
}

async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: BodyInit | null } = {}
): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: init.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
    body: init.body,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { error?: string })?.error || `Request failed (${response.status})`);
  }
  return payload as T;
}

export function getUsdaMeta() {
  return apiFetch<{ success: boolean; meta: unknown }>('/api/unit-conversion/usda/meta');
}

export async function importUsdaCsvs(foodFile: File, portionsFile: File) {
  const form = new FormData();
  form.append('food', foodFile);
  form.append('portions', portionsFile);
  return apiFetch<{ success: boolean; report: Record<string, unknown> }>(
    '/api/unit-conversion/usda/import',
    { method: 'POST', body: form }
  );
}

export function searchUsda(query: string, limit = 15) {
  return apiFetch<{ success: boolean; candidates: UsdaPortionCandidate[] }>(
    '/api/unit-conversion/usda/search',
    { method: 'POST', body: JSON.stringify({ query, limit }) }
  );
}

/** Ephemeral translate — does not persist nameEn */
export function translateIngredient(ingredientId: string) {
  return apiFetch<{ success: boolean; nameEn: string; aliasesEn: string[] }>(
    '/api/unit-conversion/translate',
    { method: 'POST', body: JSON.stringify({ ingredientId }) }
  );
}

export function previewUsdaBulkMatch(opts: {
  language?: string;
  limit?: number;
  offset?: number;
  force?: boolean;
} = {}) {
  return apiFetch<{ success: boolean; preview: Record<string, unknown> }>(
    '/api/unit-conversion/bulk-match/preview',
    { method: 'POST', body: JSON.stringify(opts) }
  );
}

export function runUsdaBulkMatch(opts: {
  language?: string;
  limit?: number;
  offset?: number;
  force?: boolean;
} = {}) {
  return apiFetch<{
    success: boolean;
    report: { runId: string; nextOffset?: number | null } & Record<string, unknown>;
  }>('/api/unit-conversion/bulk-match/run', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export function getLatestUsdaBulkMatchRun() {
  return apiFetch<{ success: boolean; run: UsdaBulkRun | null }>(
    '/api/unit-conversion/bulk-match/runs/latest'
  );
}

export function getUsdaBulkMatchRun(runId: string) {
  return apiFetch<{ success: boolean; run: UsdaBulkRun }>(
    `/api/unit-conversion/bulk-match/runs/${runId}`
  );
}

export function listUsdaBulkMatchItems(
  runId: string,
  opts: { confidence?: string; status?: string } = {}
) {
  const params = new URLSearchParams();
  if (opts.confidence) params.set('confidence', opts.confidence);
  if (opts.status) params.set('status', opts.status || 'pending');
  const qs = params.toString();
  return apiFetch<{ success: boolean; items: UsdaBulkItem[] }>(
    `/api/unit-conversion/bulk-match/runs/${runId}/items${qs ? `?${qs}` : ''}`
  );
}

export function applyUsdaBulkMatchReview(
  ingredientId: string,
  portionId: string,
  runId?: string
) {
  return apiFetch<{ success: boolean }>(
    '/api/unit-conversion/bulk-match/apply-review',
    {
      method: 'POST',
      body: JSON.stringify({ ingredientId, portionId, runId }),
    }
  );
}

export function skipUsdaBulkMatchItem(runId: string, ingredientId: string) {
  return apiFetch<{ success: boolean }>('/api/unit-conversion/bulk-match/skip', {
    method: 'POST',
    body: JSON.stringify({ runId, ingredientId }),
  });
}

export function suggestUsdaOnSave(ingredientId: string) {
  return apiFetch<{
    success: boolean;
    suggestion: {
      needed: boolean;
      reason: string;
      nameEn?: string | null;
      candidates: UsdaPortionCandidate[];
      translateError?: string;
    };
  }>('/api/unit-conversion/suggest-on-save', {
    method: 'POST',
    body: JSON.stringify({ ingredientId }),
  });
}

export interface UsdaBulkRun {
  id: string;
  status: string;
  byConfidence?: { high: number; medium: number; low: number; none: number };
  processed?: number;
  queued?: number;
  skipped?: number;
}

export interface UsdaBulkItem {
  id: string;
  ingredientId: string;
  ingredientName: string;
  nameEn?: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  bestScore?: number | null;
  suggestedPortionId?: string | null;
  suggestedUnitWeight_g?: number | null;
  candidates: UsdaPortionCandidate[];
  status: string;
}
